import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  availabilityMessage, currentLapFreshness, DRIVER_LIVE_FRESH_MS,
  formatGapToLeader, formatIntervalToAhead, formatLapTime, measurementFreshness, raceControlLabel,
} from '../driverLiveUi';
import './DriverLiveView.css';

function teamColor(driver) {
  const hex = String(driver?.teamColour || '').replace(/^#/, '');
  return /^[0-9a-f]{6}$/i.test(hex) ? `#${hex}` : '#777777';
}

function Portrait({ driver }) {
  const [failed, setFailed] = useState(false);
  return <div className="dl-portrait">
    {driver.headshotUrl && !failed
      ? <img src={driver.headshotUrl} alt="" onError={() => setFailed(true)} />
      : <span>{driver.acronym || driver.number}</span>}
  </div>;
}

function Favourite({ driver, favourite, onFavourite }) {
  const active = !!driver.acronym && favourite === driver.acronym;
  return <button type="button" className={`dl-star ${active ? 'is-active' : ''}`}
    disabled={!driver.acronym} aria-pressed={active}
    aria-label={`${active ? 'Remove' : 'Set'} ${driver.fullName || driver.acronym} as favourite driver`}
    onClick={() => onFavourite(driver.acronym)}>{active ? '★' : '☆'}</button>;
}

function Freshness({ value, label }) {
  if (!value || value.availability === 'available') return null;
  const seconds = value.ageMs == null ? null : Math.floor(value.ageMs / 1000);
  const age = seconds == null ? '' : seconds < 60 ? `${seconds}s ago` : `${Math.floor(seconds / 60)}m ago`;
  return <span className="dl-freshness">{value.availability === 'unavailable'
    ? `${label} unavailable` : `Last known ${label.toLowerCase()}${age ? ` · ${age}` : ''}`}</span>;
}

export function AccessGate({ race, error, busy, onSubmit }) {
  const [code, setCode] = useState('');
  return <section className="driver-live dl-gate">
    <Link to="/live" className="dl-back">← Back to Live</Link>
    <div className="dl-card dl-gate-card">
      <span className="dl-eyebrow">Your seat on the pit wall</span>
      <h1>DRIVER <span>LIVE</span></h1>
      <p className="dl-intro">Follow your favourite driver. Speed, gear, timing and every moment that matters.</p>
      {race?.name && <p className="dl-event-name">{race.name}</p>}
      <form onSubmit={event => { event.preventDefault(); if (!busy && code.trim()) onSubmit(code); }}>
        <label htmlFor="driver-live-code">Race code</label>
        <input id="driver-live-code" autoComplete="off" autoCapitalize="characters" spellCheck={false}
          value={code} onChange={event => setCode(event.target.value.toUpperCase())} placeholder="ENTER YOUR CODE"
          aria-describedby={error ? 'driver-live-code-error' : undefined} aria-invalid={!!error} />
        {error && <p id="driver-live-code-error" className="dl-error" role="alert">{error}</p>}
        <button className="dl-primary" disabled={busy || !code.trim()}>{busy ? 'Checking code…' : 'Open Driver Live →'}</button>
      </form>
      <p className="dl-footnote">Use the race code from the pub. No account needed.</p>
    </div>
  </section>;
}

export function AvailabilityCard({ code, detail, busy, onRetry }) {
  const [title, message] = availabilityMessage(code, detail);
  const retryable = ['TEMPORARILY_UNAVAILABLE', 'REQUEST_TIMEOUT', 'DRIVER_LIVE_NOT_OPEN', 'DRIVER_LIVE_RATE_LIMITED'].includes(code);
  return <section className="driver-live dl-gate"><div className="dl-card dl-gate-card">
    <span className="dl-eyebrow">Driver Live</span><h1>{title}</h1><p className="dl-intro">{message}</p>
    {retryable && <button className="dl-primary" onClick={onRetry} disabled={busy}>{busy ? 'Checking…' : 'Try again →'}</button>}
    <Link to="/live" className="dl-back">← Back to Live</Link>
  </div></section>;
}

export function DriverList({ drivers, favourite, onChoose, onFavourite }) {
  return <section aria-label="Choose driver">
    <div className="dl-section-heading"><h2>Choose your driver</h2><span>{drivers.length ? `${drivers.length} drivers` : 'Waiting for the field'}</span></div>
    <p className="dl-intro">Pick a driver to follow. Tap the star to remember your favourite for next time.</p>
    <div className="dl-driver-grid">{drivers.map(driver => <div key={driver.number} className="dl-driver-card" style={{ '--team': teamColor(driver) }}>
      <button className="dl-driver-choice" aria-label={`Follow ${driver.fullName || driver.acronym}`} onClick={() => onChoose(driver.number)}>
        <span className="dl-driver-position">{driver.position ? `P${driver.position}` : '—'}</span>
        <Portrait driver={driver} />
        <span className="dl-driver-name"><strong>{driver.fullName || driver.acronym || `Driver ${driver.number}`}</strong><small>{driver.team || 'Team unavailable'} · #{driver.number}</small></span>
        <span className="dl-choice-arrow" aria-hidden="true">↗</span>
      </button>
      <Favourite driver={driver} favourite={favourite} onFavourite={onFavourite} />
    </div>)}</div>
    {!drivers.length && <p className="dl-card dl-loading">Waiting for race driver data…</p>}
  </section>;
}

function Sectors({ lap, freshness }) {
  return <div className={`dl-sectors ${freshness?.stale ? 'is-stale' : ''}`}>
    {[0, 1, 2].map(index => <div key={index}><span>S{index + 1}</span><strong>{lap?.sectors?.[index] == null ? '—' : Number(lap.sectors[index]).toFixed(3)}</strong></div>)}
  </div>;
}

export function DriverPanel({ data, displayStatus, now, error, favourite, onBack, onFavourite, drivers, selected, onChoose, synchronised = false }) {
  const driver = data?.driver || drivers.find(item => item.number === selected) || { number: selected };
  const telemetry = data?.telemetry?.data;
  const position = data?.position?.data?.position;
  const intervals = data?.interval?.data;
  const telemetryFresh = measurementFreshness(data?.telemetry, now, DRIVER_LIVE_FRESH_MS.telemetry);
  const intervalFresh = measurementFreshness(data?.interval, now, DRIVER_LIVE_FRESH_MS.interval);
  const lap = data?.lap?.current?.lapTime == null ? data?.lap?.current : null;
  const lapFresh = currentLapFreshness(lap, now);
  const previous = data?.lap?.lastCompleted;
  const compound = data?.tyre?.compound;
  const throttle = telemetry?.throttle;
  const status = displayStatus || { key: 'connecting', label: 'CONNECTING' };
  return <section className="dl-focus" style={{ '--team': teamColor(driver) }}>
    <div className="dl-switchbar">
      <button className="dl-secondary" onClick={onBack}>← Drivers</button>
      <label className="dl-switch-label" htmlFor="driver-live-switch">Switch driver</label>
      <select id="driver-live-switch" value={selected || ''} onChange={event => onChoose(Number(event.target.value))}>
        {!drivers.some(item => item.number === selected) && <option value={selected}>Driver #{selected}</option>}
        {drivers.map(item => <option key={item.number} value={item.number}>{item.acronym || item.number} · {item.fullName}</option>)}
      </select>
    </div>
    <div className="dl-driver-hero">
      <Portrait key={driver.number} driver={driver} />
      <div className="dl-driver-title"><span className="dl-eyebrow">{driver.team || 'Driver telemetry'} · #{driver.number}</span><h2>{driver.fullName || driver.acronym || `Driver ${driver.number}`}</h2></div>
      <Favourite driver={driver} favourite={favourite} onFavourite={onFavourite} />
      <span className="dl-watermark" aria-hidden="true">{driver.number}</span>
    </div>
    <div className="dl-statusbar"><span className={`dl-status is-${status.key}`}><i />{status.label}</span><span>{synchronised ? 'Synced to venue broadcast' : 'Live data · may be ahead of the TV'}</span></div>
    {error && <p className="dl-notice" role="status">{error}</p>}
    <div className="dl-dashboard">
      <div className="dl-main-column">
        <section className={`dl-card dl-telemetry ${telemetryFresh.stale ? 'is-stale' : ''}`} aria-label="Car telemetry">
          <div className="dl-card-title"><h3>Car telemetry</h3><Freshness value={telemetryFresh} label="Telemetry" /></div>
          <div className="dl-speed-gear">
            <div className="dl-speed"><span className="dl-label">Speed</span><strong>{telemetry?.speed ?? '—'}</strong><span className="dl-label">km/h</span></div>
            <div className="dl-gear"><span className="dl-label">Gear</span><strong>{telemetry?.n_gear ?? '—'}</strong></div>
          </div>
          <div className="dl-throttle"><div><span className="dl-label">Throttle</span><strong>{throttle == null ? '—' : `${throttle}%`}</strong></div>
            <div className="dl-meter" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, throttle ?? 0))}%` }} /></div>
          </div>
          <div className="dl-inputs"><div><span className="dl-label">Brake</span><strong className={telemetry?.brake > 0 && !telemetryFresh.stale ? 'dl-braking' : ''}>{telemetry?.brake == null ? '—' : telemetry.brake > 0 ? 'ON' : 'OFF'}</strong></div>
            <div><span className="dl-label">Engine</span><strong>{telemetry?.rpm == null ? '—' : Math.round(telemetry.rpm).toLocaleString('en-GB')} <small>RPM</small></strong></div>
          </div>
        </section>
        <section className="dl-card dl-timing" aria-label="Lap timing">
          <div className="dl-card-title"><h3>Current lap</h3><span className="dl-lap-number">{lap ? `LAP ${lap.number}` : 'AWAITING NEXT LAP'}</span></div>
          <Sectors lap={lap} freshness={lapFresh} /><Freshness value={lapFresh} label="Current lap" />
          <div className="dl-previous"><div className="dl-card-title"><h3>Previous completed lap {previous ? `· ${previous.number}` : ''}</h3><strong>{formatLapTime(previous?.lapTime)}</strong></div><Sectors lap={previous} /></div>
        </section>
      </div>
      <div className="dl-side-column">
        <section className="dl-card" aria-label="Race position">
          <div className="dl-card-title"><h3>Race position</h3><span className="dl-label">Last known</span></div>
          <div className="dl-race-metrics"><div><span className="dl-label">Position</span><strong className="dl-position">{position == null ? '—' : `P${position}`}</strong></div>
            <div className={intervalFresh.stale ? 'is-stale' : ''}><span className="dl-label">To leader</span><strong>{formatGapToLeader(intervals?.gap_to_leader, position)}</strong><span className="dl-label">To car ahead</span><strong>{formatIntervalToAhead(intervals?.interval, position)}</strong></div>
          </div><Freshness value={intervalFresh} label="Gaps" />
          <div className="dl-tyre"><span className={`dl-compound tyre-${String(compound || '').toLowerCase()}`}>{compound ? compound[0] : '?'}</span>
            <div><strong>{compound || 'Tyre unavailable'}</strong><span>{data?.tyre?.estimatedAge == null ? 'Age unavailable' : `~${data.tyre.estimatedAge} laps · estimated age`}</span></div>
          </div>
        </section>
        <section className="dl-card dl-control" aria-label="Race control">
          <div className="dl-card-title"><h3>Race control</h3><span className="dl-label">Latest first</span></div>
          {data?.alerts?.length ? <ol>{data.alerts.slice().reverse().map((alert, index) => <li key={`${alert.date}-${index}`}>
            <div className="dl-alert-meta"><span className={raceControlLabel(alert) === 'PENALTY' ? 'dl-penalty' : ''}>{raceControlLabel(alert) || 'Race control'}</span><time>{alert.date ? new Date(alert.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</time></div>
            <p>{alert.message}</p></li>)}</ol> : <div className="dl-empty"><span aria-hidden="true">—</span><p>No messages to show yet.</p><small>Driver updates and session flags will appear here.</small></div>}
        </section>
      </div>
    </div>
  </section>;
}
